import * as z from 'zod';
import { isObject } from 'lodash-es';
import debug from 'debug';
import { shallowRef } from 'vue';
import { isDOMEvent, isComponentContext, assertVueComponent } from '../helpers';
import { ComponentContext, ConfirmSpec } from '../schemas';

const log = debug('itp:core:actions');

export type ActionContext = ComponentContext | null;

export type ActionCondition = (
  event: ActionEvent,
  args: Dictionary<any>,
) => Promise<boolean>;

export type ActionConfirmationArgs = (
  event: ActionEvent,
  args: Dictionary<any>,
) => Promise<ConfirmSpec>;

export interface ActionEvent {
  originalEvent?: Event;
  parent?: Event | ActionEvent;
  component?: ActionContext;
  scope: Dictionary<any>;
  data: any;
}

export interface ActionArgsLoader<TA> {
  (ev: ActionEvent): Promise<TA>;
}

interface ConstructorArgs<TA, TR> {
  actionArgs?: ActionArgsLoader<TA>;
  events?: EventHandlerImpl[];
  when?: ActionCondition;
  confirm?: ActionConfirmationArgs;
  displayName?: string;
}

abstract class ConditionalAction {
  readonly when?: ActionCondition;
  readonly confirm?: ActionConfirmationArgs;

  constructor(when?: ActionCondition, confirm?: ActionConfirmationArgs) {
    this.when = when;
    this.confirm = confirm;
  }

  protected get fullName() {
    return this.constructor.name;
  }

  async isExecutionPrevented(event: ActionEvent, args: Dictionary<any>) {
    let result = false;

    if (this.when) {
      log(
        '[%s] evaluating when expression - event: %o - args: %o',
        this.fullName,
        event,
        args,
      );

      result = !(await this.when(event, args));
    }

    if (!result && this.confirm) {
      const confirm = await this.confirm(event, args);
      log('[%s] asking user confirmation - %o', this.fullName, confirm);
      assertVueComponent(event.component);
      result =
        (await (event.component as any).$imodal.confirm(confirm)) !== true;
    }

    if (result) {
      log('[%s] execution is prevented.', this.fullName);
      return result;
    }

    return result;
  }
}

export abstract class Action<TA, TR> extends ConditionalAction {
  readonly actionArgs?: (ev: ActionEvent) => Promise<TA>;
  readonly events?: EventHandlerImpl[];
  readonly displayName?: string;
  private _currentEvent = shallowRef<ActionEvent | null>(null);

  constructor(args?: ConstructorArgs<TA, TR>) {
    super(args ? args.when : undefined, args ? args.confirm : undefined);
    this.actionArgs = args ? args.actionArgs : undefined;
    this.events = args ? args.events : undefined;
    this.displayName = args ? args.displayName : undefined;
  }

  abstract handle(event: ActionEvent, args: TA | null): Promise<TR | null>;

  static get args_schema() {
    return {
      name: z.literal('foo'),
      args: z.object({}),
    };
  }

  get isRunning() {
    return !!this._currentEvent.value;
  }

  protected get fullName() {
    const cname = this.constructor.name;
    return this.displayName ? `${cname}[${this.displayName}]` : cname;
  }

  async triggerFromDOM(
    component: ActionContext,
    event: Event,
    scope: Dictionary<any>,
  ) {
    if (this._currentEvent.value) {
      log(
        '[%s] Action already running, skipping duplicate trigger.',
        this.fullName,
      );
      return null;
    }
    try {
      let data: any = {};
      if (!isDOMEvent(event)) {
        data = event ?? {};
      }

      const ev = { originalEvent: event, component, scope, data };
      if (await this.isExecutionPrevented(ev, {})) {
        return null;
      }
      const args: TA | null = this.actionArgs
        ? await this.actionArgs(ev)
        : null;
      this._currentEvent.value = ev;
      log(
        '[%s] TRIGGER_FROM_DOM - event: %o - args: %o (%o)',
        this.fullName,
        event,
        args,
        this,
      );
      return await this.handle(ev, args);
    } finally {
      this._currentEvent.value = null;
    }
  }

  async triggerFromParent(parent: ActionEvent, data: Dictionary<any>) {
    if (this._currentEvent.value) {
      log(
        '[%s] Action already running, skipping duplicate trigger.',
        this.fullName,
      );
      return null;
    }
    try {
      const ev = {
        originalEvent: parent.originalEvent,
        component: parent.component,
        scope: parent.scope,
        parent,
        data,
      };
      if (await this.isExecutionPrevented(ev, {})) {
        return null;
      }
      const args: TA | null = this.actionArgs
        ? await this.actionArgs(ev)
        : null;
      this._currentEvent.value = ev;
      log('TRIGGER_FROM_PARENT [%s] args: %o (%o)', this.fullName, args, this);
      return await this.handle(ev, args);
    } finally {
      this._currentEvent.value = null;
    }
  }

  async dispatch(event: string, args: Dictionary<any>) {
    if (!this.events || !this.events.length) {
      log(
        'DISPATCH %s [%s] Skipping, no subscribers. args=%o (action=%o)',
        event,
        this.fullName,
        args,
        this,
      );
      return;
    }
    if (!this._currentEvent.value) {
      throw new Error(`Cannot dispatch ${event}: missing current event.`);
    }

    const data = isObject(args)
      ? { ...this._currentEvent.value.data, ...args }
      : this._currentEvent.value.data;

    const ev = { ...this._currentEvent.value, data };

    log(
      'DISPATCH %s [%s] - event: %o, args: %o',
      event,
      this.fullName,
      ev,
      args,
    );

    for (const handler of this.events) {
      if (handler.event == event) {
        await handler.execute(ev, args);
      }
    }
  }
}

export interface EventHandlerOptions {
  event: string;
  component?: ActionContext;
  action: Action<any, any>;
  disabled?: boolean;
  when?: ActionCondition;
  confirm?: ActionConfirmationArgs;
  displayName?: string;
}

export class EventHandlerImpl extends ConditionalAction {
  readonly event: string;
  readonly ctx?: ActionContext;
  readonly action: Action<any, any>;
  readonly disabled?: boolean;
  readonly displayName?: string;

  constructor(options: EventHandlerOptions) {
    super(options.when, options.confirm);
    this.event = options.event;
    this.ctx = options.component;
    this.action = options.action;
    this.disabled = options.disabled;
    this.displayName = options ? options.displayName : '';
  }

  get isRunning() {
    return this.action.isRunning;
  }

  protected get fullName() {
    let prefix = '';
    if (this.ctx && (this.ctx as any).$uid) {
      prefix = (this.ctx as any).$uid + '/';
    }
    const cname = prefix + this.constructor.name + '[' + this.event + ']';
    return this.displayName ? `${cname}::${this.displayName}` : cname;
  }

  async execute(event: ActionEvent, args: Dictionary<any>) {
    if (this.disabled) {
      return;
    }
    if (await this.isExecutionPrevented(event, args)) {
      return null;
    }
    log('EXECUTE [%s] event: %o', this.fullName, event);
    log('EXECUTE [%s] args: %o', this.fullName, args);
    return await this.action.triggerFromParent(event, args);
  }

  async executeFromDOM(
    component: ActionContext,
    event: Event,
    scope: Dictionary<any>,
  ) {
    let data: any = {};
    if (!isDOMEvent(event)) {
      data = event ?? {};
    }

    return await this.execute(
      {
        component,
        originalEvent: event,
        scope,
        data,
      },
      data,
    );
  }
}

export interface EventHandlerGroupOptions {
  event: string;
  handlers: EventHandlerImpl[];
  displayName?: string;
}

export class EventHandlerGroup {
  readonly event: string;
  readonly ctx?: ActionContext;
  readonly handlers: EventHandlerImpl[];
  readonly displayName?: string;

  constructor(options: EventHandlerGroupOptions) {
    this.event = options.event;
    this.handlers = options.handlers;
    this.displayName = options ? options.displayName : '';
  }

  protected get fullName() {
    const cname = this.constructor.name + ':' + this.event;
    return this.displayName ? `${cname}#${this.displayName}` : cname;
  }

  async executeFromDOM(
    component: ActionContext,
    event: Event,
    scope: Dictionary<any>,
  ) {
    try {
      let data: any = {};
      if (!isDOMEvent(event)) {
        data = event ?? {};
      }
      const ev = { component, originalEvent: event, scope, data };

      log(
        '[%s] executeFromDOM - event: %o - args: %o',
        this.fullName,
        ev,
        data,
      );

      for (const h of this.handlers) {
        await h.execute(ev, data);
      }
    } catch (error) {
      if (isComponentContext(component)) {
        component.handleError(error);
      }
    }
  }
}
